import React from 'react';
import { Icon } from '../../icons/Icon';

import './SearchInput.css';

export type SearchInputSize = 'sm' | 'md' | 'lg';

export interface SearchInputProps {
  /** Current value */
  value?: string;
  /** Default value for uncontrolled mode */
  defaultValue?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Callback when search is submitted (Enter key) */
  onSubmit?: (value: string) => void;
  /** Callback when input is cleared */
  onClear?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Keyboard shortcut hint (e.g., "⌘K") */
  shortcut?: string;
  /** Size variant */
  size?: SearchInputSize;
  /** Full width mode */
  fullWidth?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Show clear button when there's a value */
  clearable?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

export const SearchInput = React.memo(function SearchInput({
  value,
  defaultValue = '',
  onChange,
  onSubmit,
  onClear,
  placeholder = 'Search...',
  shortcut,
  size = 'md',
  fullWidth = false,
  disabled = false,
  clearable = true,
  className = '',
  ariaLabel = 'Search',
}: SearchInputProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit(currentValue);
    }
    if (e.key === 'Escape' && currentValue) {
      handleClear();
    }
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalValue('');
    }
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div
      className={`search-input ${className}`.trim()}
      data-size={size}
      data-full-width={fullWidth}
      data-disabled={disabled}
    >
      <Icon name="Search" size={size === 'sm' ? 16 : 20} className="search-input__icon" />
      <input
        ref={inputRef}
        type="text"
        value={currentValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className="search-input__field"
      />
      {clearable && currentValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className="search-input__clear"
          aria-label="Clear search"
        >
          <Icon name="X" size={14} />
        </button>
      )}
      {shortcut && !currentValue && (
        <span className="search-input__shortcut">{shortcut}</span>
      )}
    </div>
  );
});
